// src/pages/PrivacyPolicy.jsx
import React from "react";

const PAGE_GRADIENT =
  "radial-gradient(circle at top, #26266b 0, #050514 55%, #000 100%)";

export default function PrivacyPolicy() {
  return (
    <div
      className="min-h-screen text-slate-100 px-4 py-10"
      style={{ background: PAGE_GRADIENT }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-pink-400/80 mb-2">
            Legal
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-slate-400">
            Last updated: <span className="font-medium text-slate-200">27 November 2025</span>
          </p>
        </header>

        <div className="space-y-8 text-sm md:text-base leading-relaxed text-slate-200">
          {/* 1. Who we are */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              1. Who we are
            </h2>
            <p className="mb-3">
              This Privacy Policy explains how <span className="font-semibold">PixelAds.ai</span>{" "}
              ("PixelAds", "we", "us", or "our") collects, uses, and protects your
              personal data when you use our website, app, and related services
              (together, the "Service").
            </p>
            <p>
              If you have questions about this Policy or how we handle personal
              data, you can contact us at{" "}
              <span className="font-mono">contact@pixelads.ai</span>.
            </p>
          </section>

          {/* 2. Scope */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              2. Scope of this Policy
            </h2>
            <p className="mb-3">
              This Policy applies when you:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>Visit our website or marketing pages</li>
              <li>Create an account and sign in with Clerk or Google</li>
              <li>
                Use the Product Dashboard and generation tools (for example,
                background replacer, object remover, ad creatives, video
                generators)
              </li>
              <li>Subscribe to a paid plan or trial</li>
              <li>Communicate with us by email or support channels</li>
            </ul>
            <p className="mt-3">
              It does not apply to third-party websites, apps, or integrations
              that are not operated by us, even if they are connected to
              PixelAds. Those services have their own privacy policies.
            </p>
          </section>

          {/* 3. Information we collect */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              3. Information we collect
            </h2>

            <h3 className="font-semibold mb-2">3.1 Information you provide</h3>
            <ul className="list-disc list-inside space-y-1 mb-3 text-slate-200/90">
              <li>
                <span className="font-semibold">Account data:</span> name,
                email, profile image (if provided via Clerk or Google),
                authentication identifiers.
              </li>
              <li>
                <span className="font-semibold">Subscription and billing:</span>{" "}
                plan type, billing country, partial payment details handled by
                Stripe (we do not store full card numbers).
              </li>
              <li>
                <span className="font-semibold">Project and content data:</span>{" "}
                product URLs, titles, descriptions, images, videos, prompts, and
                any other content you upload or provide for analysis or
                generation.
              </li>
              <li>
                <span className="font-semibold">Support and communication:</span>{" "}
                emails, in-app messages, and feedback you send to us.
              </li>
            </ul>

            <h3 className="font-semibold mb-2">3.2 Information collected automatically</h3>
            <ul className="list-disc list-inside space-y-1 mb-3 text-slate-200/90">
              <li>
                <span className="font-semibold">Usage data:</span> pages and
                screens you visit, buttons you click, features you use, time
                spent in the app, referral pages.
              </li>
              <li>
                <span className="font-semibold">Device and log data:</span>{" "}
                IP address, browser type, operating system, approximate
                location, timestamps, error logs, and diagnostic information.
              </li>
            </ul>

            <h3 className="font-semibold mb-2">3.3 Cookies and similar technologies</h3>
            <p className="mb-2">
              We use cookies and similar technologies to keep you signed in,
              remember preferences, measure performance, and run product
              experiments. Cookie consent is managed through our consent tool
              (for example, Cookiebot), where required by law.
            </p>
            <p>
              You can manage cookies in your browser settings, but disabling
              certain cookies may affect the functionality of the Service.
            </p>
          </section>

          {/* 4. How we use information */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              4. How we use your information
            </h2>
            <ul className="list-disc list-inside space-y-2 text-slate-200/90">
              <li>
                <span className="font-semibold">Provide and operate the Service:</span>{" "}
                authenticate you with Clerk, load your projects and assets,
                generate images and videos using vendors such as Phot.AI and
                TopView, and store them via Cloudinary.
              </li>
              <li>
                <span className="font-semibold">Billing and subscriptions:</span>{" "}
                process payments, manage renewals, enforce plan quotas, and
                handle add-ons through Stripe.
              </li>
              <li>
                <span className="font-semibold">Improve and protect PixelAds:</span>{" "}
                monitor usage, debug issues, run analytics and experiments,
                detect spam or abuse, and protect our infrastructure.
              </li>
              <li>
                <span className="font-semibold">Communicate with you:</span>{" "}
                send transactional emails (for example, login links, invoices,
                usage alerts), respond to support requests, and send product
                updates or marketing messages where permitted.
              </li>
              <li>
                <span className="font-semibold">Legal and compliance:</span>{" "}
                keep records required by law, respond to lawful requests from
                authorities, and enforce our Terms and policies.
              </li>
            </ul>
          </section>

          {/* 5. Legal bases */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              5. Legal bases (EEA / UK users)
            </h2>
            <p className="mb-2">
              Where data protection laws like GDPR apply, we rely on the
              following legal bases:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>Performance of a contract (providing the Service you signed up for)</li>
              <li>Legitimate interests (for example, improving and securing our Service)</li>
              <li>Consent (for non-essential cookies and certain marketing communications)</li>
              <li>Legal obligations (for example, tax and accounting requirements)</li>
            </ul>
          </section>

          {/* 6. Sharing */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              6. How we share your information
            </h2>
            <p className="mb-3">
              We do not sell your personal data. We share it with trusted
              service providers only when needed to run PixelAds, for example:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-3 text-slate-200/90">
              <li>Clerk (authentication and user management)</li>
              <li>Stripe (payments and subscription billing)</li>
              <li>Phot.AI, TopView, Placid (image and video processing)</li>
              <li>Cloudinary and hosting providers (media and app hosting)</li>
              <li>Analytics, monitoring, and feedback tools (for example, Hotjar)</li>
              <li>Email delivery and notification providers</li>
            </ul>
            <p className="mb-2">
              These providers only receive the minimum data they need and are
              bound by contracts to protect it and use it only on our
              instructions.
            </p>
            <p>
              We may also disclose information if required by law or in
              connection with a merger, acquisition, or sale of assets. In such
              cases, we will ensure appropriate safeguards are in place.
            </p>
          </section>

          {/* 7. International transfers */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              7. International data transfers
            </h2>
            <p>
              We are based in the EU, but some of our providers are located in
              other countries, including the United States. When personal data
              is transferred outside the EEA or UK, we use appropriate safeguards
              such as Standard Contractual Clauses or equivalent mechanisms to
              protect your data.
            </p>
          </section>

          {/* 8. Retention */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              8. Data retention
            </h2>
            <p className="mb-2">
              We keep your personal data only for as long as necessary to:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-2 text-slate-200/90">
              <li>Provide and maintain your account and subscription</li>
              <li>Comply with legal, tax, and accounting obligations</li>
              <li>Resolve disputes and enforce our agreements</li>
            </ul>
            <p>
              When data is no longer needed, we will delete it or anonymize it.
              We may keep anonymized or aggregated data for analytics and
              product improvement.
            </p>
          </section>

          {/* 9. Security */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              9. Data security
            </h2>
            <p>
              We use technical and organizational measures to protect your data,
              such as encryption in transit, access controls, monitoring, and
              regular backups. No system is completely secure, and we cannot
              guarantee absolute security. You are responsible for safeguarding
              your login credentials and for any activity under your account.
            </p>
          </section>

          {/* 10. Your rights */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              10. Your rights
            </h2>
            <p className="mb-2">
              Depending on your location, you may have rights over your personal
              data, including:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-2 text-slate-200/90">
              <li>Access and obtain a copy of your data</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Request deletion of your data in certain situations</li>
              <li>Restrict or object to certain types of processing</li>
              <li>Receive your data in a portable format</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
            <p>
              To exercise these rights, contact us at{" "}
              <span className="font-mono">contact@pixelads.ai</span>. We may
              need to verify your identity before responding. You may also have
              the right to lodge a complaint with your local data protection
              authority.
            </p>
          </section>

          {/* 11. Children */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              11. Children
            </h2>
            <p>
              PixelAds is designed for adults working on ecommerce and
              marketing. We do not knowingly collect personal data from children
              under 16. If you believe a child has provided us with personal
              data, please contact us so we can delete it.
            </p>
          </section>

          {/* 12. Changes */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              12. Changes to this Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. When we make
              material changes, we will update the "Last updated" date at the
              top and, where appropriate, notify you in the app or by email.
              Your continued use of the Service after changes take effect means
              you accept the updated Policy.
            </p>
          </section>

          {/* 13. Contact */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              13. Contact us
            </h2>
            <p>
              For privacy questions or requests, you can email{" "}
              <span className="font-mono">contact@pixelads.ai</span>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
